import { Injectable } from '@angular/core';
import { map, switchMap, take } from 'rxjs';
import { uuidV4 } from '../../utils/uuidUtil';
import { ECommerceAppleCardTokenRequest, ECommerceApplePayToken } from '../api/api.models';
import { ApiService } from '../api/api.service';
import { ErrorService } from '../error/error.service';
import { ExternalDataService } from '../external-data/external-data.service';

@Injectable({
  providedIn: 'root',
})
export class ApplePayService {
  // To be configurable in the future (PG-3572)
  private readonly SUPPORTED_NETWORKS = [
    'amex',
    'bancomat',
    'bancontact',
    'cartesBancaires',
    'chinaUnionPay',
    'dankort',
    'discover',
    'eftpos',
    'electron',
    'elo',
    'girocard',
    'interac',
    'jcb',
    'mada',
    'maestro',
    'masterCard',
    'mir',
    'privateLabel',
    'visa',
    'vPay',
  ];

  constructor(
    private apiService: ApiService,
    private externalDataService: ExternalDataService,
    private errorService: ErrorService
  ) {}

  tokenize() {
    if (!window.ApplePaySession) {
      console.error('ApplePaySession not available.');
      return;
    }

    const request = {
      countryCode: 'EE',
      currencyCode: 'EUR',
      supportedNetworks: this.SUPPORTED_NETWORKS,
      merchantCapabilities: ['supports3DS'],
      total: { label: 'Sfey Pay', amount: '0.00' },
    };

    const session = new window.ApplePaySession(3, request);

    session.onvalidatemerchant = () => {
      this.apiService.getAppleValidationObject().subscribe((response: any) => {
        session.completeMerchantValidation(response);
      });
    };

    session.onpaymentmethodselected = () => {
      session.completePaymentMethodSelection({ newTotal: request.total });
    };

    session.onpaymentauthorized = (event) => {
      const token = event.payment.token as ECommerceApplePayToken;

      this.doTokenization(token).subscribe({
        next: (response) => {
          session.completePayment({ status: window.ApplePaySession!.STATUS_SUCCESS });
          this.externalDataService.sendParentSuccessResponse(response);
        },
        error: (error) => {
          session.completePayment({ status: window.ApplePaySession!.STATUS_FAILURE });
          this.errorService.handleApiError(error);
        },
      });
    };

    session.oncancel = () => {
      this.errorService.handleAppleCancellation();
    };

    session.begin();
  }

  private doTokenization(token: ECommerceApplePayToken) {
    return this.externalDataService.$data
      .pipe(take(1))
      .pipe(
        map((externalData) => {
          const request: ECommerceAppleCardTokenRequest = {
            token: token,
            companyId: +externalData.companyId,
            datetime: externalData.datetime,
            hmacFields: externalData.hmacFields,
            hmac: externalData.hmac,
          };

          return request;
        })
      )
      .pipe(
        switchMap((request) => {
          const uuid = uuidV4();
          return this.apiService.tokenizeFromApplePayPayment(uuid, request);
        })
      );
  }
}
